import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Shield, CheckCircle } from "lucide-react";
import { services } from "../data/services";
import { cities } from "../data/cities";

export const Footer = () => {
  // Group cities by department for better organization
  const citiesColumn1 = cities.slice(0, 17);
  const citiesColumn2 = cities.slice(17, 34);
  const citiesColumn3 = cities.slice(34);

  return (
    <footer className="bg-zinc-950 text-white">
      {/* Main Footer */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <Link to="/" className="block mb-6">
              <img 
                src="/serrurier-lieusaint-logo.jpg" 
                alt="Serrurier Lieusaint dépannage serrurerie 24h/24"
                className="h-24 md:h-28 w-auto object-contain"
              />
            </Link>
            <p className="text-sm text-zinc-400 mb-4">
              Vincent VALLES – Artisan serrurier
            </p>

            <div className="space-y-4 text-zinc-300">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p>23 Avenue René Cassin</p>
                  <p>77127 Lieusaint</p>
                </div>
              </div>

              <a 
                href="tel:0668032561" 
                className="flex items-center gap-3 hover:text-yellow-400 transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5 text-yellow-400" />
                <span className="font-bold">06 68 03 25 61</span>
              </a>

              <a 
                href="mailto:serrurierlieusaint@gmail.com" 
                className="flex items-center gap-3 hover:text-yellow-400 transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-5 h-5 text-yellow-400" />
                <span>serrurierlieusaint@gmail.com</span>
              </a>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-yellow-400" />
                <span>24h/24 – 7j/7</span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-800">
              <p className="text-sm text-zinc-400">
                <strong>SIRET :</strong> 94505578800018
              </p>
              <p className="text-sm text-zinc-400">
                Société par actions simplifiée
              </p>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-lg uppercase tracking-tight mb-6 font-heading text-yellow-400">
              Nos Services
            </h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.id}>
                  <Link 
                    to={`/service/${service.slug}`}
                    className="text-zinc-300 hover:text-yellow-400 transition-colors text-sm flex items-center gap-2"
                    data-testid={`footer-service-${service.slug}`}
                  >
                    <CheckCircle className="w-4 h-4 text-yellow-400" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <h3 className="font-bold text-lg uppercase tracking-tight mb-4 font-heading text-yellow-400">
                Garanties
              </h3>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-yellow-400" />
                  Intervention rapide 15-30 min
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-yellow-400" />
                  Devis avant intervention
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-yellow-400" />
                  Disponible 24h/24 et 7j/7
                </li>
              </ul>
            </div>
          </div>

          {/* Cities Column 1 */}
          <div>
            <h3 className="font-bold text-lg uppercase tracking-tight mb-6 font-heading text-yellow-400">
              Zones d'intervention
            </h3>
            <ul className="space-y-1.5 text-sm">
              {citiesColumn1.map((city) => (
                <li key={city.slug}>
                  <Link 
                    to={`/serrurier/${city.slug}`}
                    className="text-zinc-400 hover:text-yellow-400 transition-colors"
                    data-testid={`footer-city-${city.slug}`}
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Cities Column 2 & 3 */}
          <div>
            <h3 className="font-bold text-lg uppercase tracking-tight mb-6 font-heading text-yellow-400 opacity-0">
              Zones
            </h3>
            <ul className="space-y-1.5 text-sm mb-6">
              {citiesColumn2.map((city) => (
                <li key={city.slug}>
                  <Link 
                    to={`/serrurier/${city.slug}`}
                    className="text-zinc-400 hover:text-yellow-400 transition-colors"
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
            <ul className="space-y-1.5 text-sm">
              {citiesColumn3.map((city) => (
                <li key={city.slug}>
                  <Link 
                    to={`/serrurier/${city.slug}`}
                    className="text-zinc-400 hover:text-yellow-400 transition-colors"
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-zinc-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-zinc-400">
            <p>
              © {new Date().getFullYear()} Serrurier Lieusaint – Tous droits réservés
            </p>
            <div className="flex gap-6">
              <Link to="/mentions-legales" className="hover:text-yellow-400 transition-colors">
                Mentions légales
              </Link>
              <Link to="/politique-confidentialite" className="hover:text-yellow-400 transition-colors">
                Politique de confidentialité
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
